/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1669766400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Nov 30 2022 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "google-pubsub",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Connect to your Google Cloud account",
          "depth": 1,
          "id": "connect-to-your-google-cloud-account"
        }, {
          "value": "Project details",
          "depth": 1,
          "id": "project-details"
        }, {
          "value": "Grant permissions",
          "depth": 1,
          "id": "grant-permissions"
        }, {
          "value": "Syncing data",
          "depth": 0,
          "id": "syncing-data"
        }, {
          "value": "Configure your events trigger",
          "depth": 1,
          "id": "configure-your-events-trigger"
        }, {
          "value": "Choose your topic",
          "depth": 1,
          "id": "choose-your-topic"
        }, {
          "value": "Customize your message",
          "depth": 1,
          "id": "customize-your-message"
        }, {
          "value": "Configure optional message properties",
          "depth": 1,
          "id": "configure-optional-message-properties"
        }, {
          "value": "Configure initial sync behavior",
          "depth": 1,
          "id": "configure-initial-sync-behavior"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      h3: "h3",
      strong: "strong",
      a: "a",
      img: "img",
      code: "code",
      ul: "ul",
      li: "li",
      h4: "h4",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      pre: "pre",
      span: "span",
      h5: "h5"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch integrates directly with Google Cloud Pub/Sub to support high-throughput, distributed, or asynchronous workloads, letting you build a custom connector to your internal systems."
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsx(_components.h3, {
        id: "connect-to-your-google-cloud-account",
        children: "Connect to your Google Cloud account"
      }), "\n", _jsxs(_components.p, {
        children: ["When setting up the Google Cloud Pub/Sub destination for the first time, you need to enter your ", _jsx(_components.strong, {
          children: "Google Cloud Credentials"
        }), " to give Hightouch access to your Google Cloud account. Hightouch needs permission to send messages to Google Cloud Pub/Sub on your behalf. Consult ", _jsx(_components.a, {
          href: "/security/gcp",
          children: "our GCP documentation"
        }), " for guidance on how to create a Google Cloud credential."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-pubsub-set-up.png",
          alt: "Entering Google Cloud Credentials in Hightouch"
        })
      }), "\n", _jsx(_components.h3, {
        id: "project-details",
        children: "Project details"
      }), "\n", _jsxs(_components.p, {
        children: ["First, you need to enter your ", _jsx(_components.strong, {
          children: "GCP Project ID"
        }), " and ", _jsx(_components.strong, {
          children: _jsx(_components.a, {
            href: "https://cloud.google.com/pubsub/docs/reference/service_apis_overview#list_of_regional_endpoints",
            children: "Regional API Endpoint"
          })
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "grant-permissions",
        children: "Grant permissions"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch believes in the principle of least privilege. We ask for no more permissions than necessary. For the Google Cloud Pub/Sub destination, Hightouch requires ", _jsx(_components.code, {
          children: "roles/pubsub.publisher"
        }), " and ", _jsx(_components.code, {
          children: "roles/pubsub.viewer"
        }), ". Ensure you run the command in your Google Cloud Console to grant Hightouch's service account the correct permissions."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-pubsub-grant-permissions.png",
          alt: "Grant permissions to Google Cloud service account"
        })
      }), "\n", _jsx(_components.h2, {
        id: "syncing-data",
        children: "Syncing data"
      }), "\n", _jsx(_components.p, {
        children: "Once you've authenticated your Google Cloud account in Hightouch, entered project details, and granted the correct permissions, you can configure a sync that sends messages whenever rows are added, changed, or removed in your model."
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-events-trigger",
        children: "Configure your events trigger"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch monitors your data model for added, changed, and removed rows. In this step, you specify which of these events should trigger message publishing."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-apache-kafka-events-trigger.png",
          alt: "Events to trigger message publishing"
        })
      }), "\n", _jsx(_components.h3, {
        id: "choose-your-topic",
        children: "Choose your topic"
      }), "\n", _jsx(_components.p, {
        children: "In this step, you choose which topics to publish the messages to. Hightouch allows you to sync to existing topics that are already in your Google Cloud Pub/Sub."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-pubsub-choose-topic.png",
          alt: "Toggle use column to send message to multiple queues"
        })
      }), "\n", _jsx(_components.h3, {
        id: "customize-your-message",
        children: "Customize your message"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-apache-kafka-customize-message-data.png",
          alt: "Customize message data options"
        })
      }), "\n", _jsx(_components.p, {
        children: "In this step, you tell Hightouch how to build the JSON message data object using data from your model."
      }), "\n", _jsx(_components.p, {
        children: "This destination offers three methods of composing a JSON object:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#use-json-editor",
            children: "Using a JSON editor"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#use-one-column-from-model",
            children: "Using one column from a model"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#use-multiple-columns-from-model",
            children: "Using multiple columns from a model"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "use-json-editor",
        children: "Use JSON editor"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-apache-kafka-json-editor.png",
          alt: "Selecting the JSON editor method in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["With the JSON editor, you can compose any JSON object using the ", _jsx(_components.a, {
          href: "https://shopify.github.io/liquid/",
          children: "Liquid template language"
        }), ". This is particularly useful for complex message data bodies containing nested objects and arrays, which can sometimes be difficult to model entirely in SQL."]
      }), "\n", _jsx(_components.p, {
        children: "Suppose your data model looks like this:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "full_name"
            }), _jsx(_components.th, {
              children: "age"
            }), _jsx(_components.th, {
              children: "email_address"
            }), _jsx(_components.th, {
              children: "phone_number"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "John Doe"
            }), _jsx(_components.td, {
              children: "30"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "john@example.com"
              })
            }), _jsx(_components.td, {
              children: "+14158675309"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: "And you want your message data like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"John Doe\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"age\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "30"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"contact_info\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"email\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"value\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"john@example.com\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"phone\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"value\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"+14158675309\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "Your Liquid template should look like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-txt",
          children: "{\n  \"name\": \"{{row.full_name}}\",\n  \"age\": {{row.age}},\n  \"contact_info\": [\n    {\n      \"type\": \"email\",\n      \"value\": \"{{row.email_address}}\"\n    },\n    {\n      \"type\": \"phone\",\n      \"value\": \"{{row.phone_number}}\"\n    }\n  ]\n}\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This makes it so you can reference any column using the syntax ", _jsx(_components.code, {
          children: "{{row.column_name}}"
        }), ". You can also use advanced Liquid features to incorporate control flow and loops into your dynamic message data."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "When injecting strings into your JSON object, be sure to surround the Liquid\ntag in double quotes."
        })
      }), "\n", _jsx(_components.h4, {
        id: "use-one-column-from-model",
        children: "Use one column from model"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-apache-kafka-one-column-from-model.png",
          alt: "Selecting using one column from the model as the JSON construction method in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "If you're already storing JSON data in your source, or if you have the ability to construct a JSON object using SQL, you can select one column in your model that already contains the full message data."
      }), "\n", _jsx(_components.p, {
        children: "This setting is commonly used when syncing web events that have already been collected and stored as JSON objects in your database."
      }), "\n", _jsx(_components.h4, {
        id: "use-multiple-columns-from-model",
        children: "Use multiple columns from model"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-apache-kafka-multiple-columns-from-model.png",
          alt: "Selecting using multiple columns from the model as the JSON construction method in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "For the simplest use cases, Hightouch can construct a JSON object with key/value pairs based on multiple columns in your model."
      }), "\n", _jsx(_components.p, {
        children: "Suppose your model looks like this:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "email"
            }), _jsx(_components.th, {
              children: "first_name"
            }), _jsx(_components.th, {
              children: "last_name"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "alice.doe@example.com"
              })
            }), _jsx(_components.td, {
              children: "Alice"
            }), _jsx(_components.td, {
              children: "Doe"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "bob.doe@example.com"
              })
            }), _jsx(_components.td, {
              children: "Bob"
            }), _jsx(_components.td, {
              children: "Doe"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "carol.doe@example.com"
              })
            }), _jsx(_components.td, {
              children: "Carol"
            }), _jsx(_components.td, {
              children: "Doe"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "The field mapping in the screenshot above would generate the following message data for the first row:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"customer_first_name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Alice\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"customer_last_name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Doe\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"customer_email\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"alice.doe@example.com\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["You can use the field mapper to rename fields. For example, ", _jsx(_components.code, {
            children: "first_name"
          }), " can\nbe mapped to ", _jsx(_components.code, {
            children: "customer_first_name"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "configure-optional-message-properties",
        children: "Configure optional message properties"
      }), "\n", _jsx(_components.p, {
        children: "Along with your row data in JSON format, you can also optionally include an ordering key to configure the order your topic receives your message and metadata fields as additional attributes."
      }), "\n", _jsx(_components.h5, {
        id: "orderingkey",
        children: _jsx(_components.code, {
          children: "orderingKey"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This is a string field that ensure subscribers receive messages ", _jsx(_components.a, {
          href: "https://cloud.google.com/pubsub/docs/ordering",
          children: "in a specific order"
        }), ". Hightouch automatically tries to cast the value to a string. If we can't cast the value to a string then it is sent as ", _jsx(_components.code, {
          children: "null"
        }), "."]
      }), "\n", _jsx(_components.h5, {
        id: "attributes",
        children: _jsx(_components.code, {
          children: "attributes"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This is an object containing key/value pairs of custom mapping fields. Values can be text or byte strings. Attributes can be text strings or byte strings. You can have at most 100 attributes per message. Attribute keys should not start with ", _jsx(_components.code, {
          children: "goog"
        }), " and should not exceed 256 bytes."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-initial-sync-behavior",
        children: "Configure initial sync behavior"
      }), "\n", _jsx(_components.p, {
        children: "In this step, you tell Hightouch how to handle rows present in your model results during the first sync run."
      }), "\n", _jsx(_components.p, {
        children: "Certain workflows may require performing a backfill of all rows during the initial sync. For other use cases, you might only want to send messages in response to future data changes."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-apache-kafka-initial-sync-behaviour.png",
          alt: "Declaring how to handle initial sync behavior in Hightouch"
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
